import { ButtonNew as ButtonNewV2, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import Link from 'root/src/client/web/base/Link';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import * as CreatorDataStat from 'root/src/client/web/record/CreatorData/components/CreatorDataStat';
import { useCreatorDataInfo } from 'root/src/client/web/record/CreatorData/hooks/useCreatorDataInfo';
import { useCreatorDataActionButtons } from 'root/src/client/web/record/CreatorData/hooks/useCreatorDataButtons';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { BecomeCreatorButton } from 'root/src/client/web/auth/features/BecomeCreatorButton';
import { useUserData } from 'root/src/client/v2/userData/hooks/useUserData';
import { useIsBrand } from 'root/src/client/v2/auth/hooks/useIsBrand';
import { FaRegHeart, FaHeart } from 'react-icons/fa';
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import { twMerge } from 'tailwind-merge';
import { useCreatorLiveStream } from 'root/src/client/web/record/CreatorData/hooks/useCreatorLiveStream';
import { LiveIndicatorBadge } from 'root/src/client/web/record/CreatorData/components/LiveIndicatorBadge';
const CreatorDataAvatar = ({ image, alt }) => {
    return (<div className="border-2 rounded-full p-1.5 relative w-24 h-24">
			{image ? <img className="rounded-full drop-shadow-avatar w-full h-full" src={image} alt={alt || "Creator's Avatar"}/> : <LoadingCircles />}
		</div>);
};
const CreatorDataNameSkeleton = () => {
    return (<span className="w-24 h-8 rounded-xl bg-gradient-to-bl from-light-4 to-light-5 self-center sm:self-start "/>);
};
const CreatorDataName = ({ name }) => {
    if (!name) {
        return <CreatorDataNameSkeleton />;
    }
    return (<span className="text-3xl font-mono font-bold text-center sm:text-left">{name}</span>);
};
const useCreatorDataStats = () => {
    const { isAdmin } = useIsAdmin();
    return {
        isAdmin,
    };
};
const CreatorDataStats = ({ followers, ccv, isOwnProfile }) => {
    const { isAdmin } = useCreatorDataStats();
    const formattedFollowers = new Intl.NumberFormat('en-US').format(followers || 0);
    return (<div className="flex gap-5">
			<CreatorDataStat.Root>
				<CreatorDataStat.Label>
					Followers:
				</CreatorDataStat.Label>
				<CreatorDataStat.Value>
					{typeof followers !== 'undefined' ? formattedFollowers : <div className="w-8 h-8"><LoadingCircles /></div>}
				</CreatorDataStat.Value>
			</CreatorDataStat.Root>
			{(isAdmin || isOwnProfile) && (<CreatorDataStat.Root>
					<CreatorDataStat.Label>
						CCV:
					</CreatorDataStat.Label>
					<CreatorDataStat.Value>
						{typeof ccv !== 'undefined' ? ccv : '?'}
					</CreatorDataStat.Value>
				</CreatorDataStat.Root>)}
		</div>);
};
const CreatorDataSocialLinks = ({ links }) => {
    if (!links || !links.length)
        return null;
    return (<ul className="flex flex-initial flex-wrap gap-x-10 gap-y-4 py-2 items-start justify-start">
			{links.map(({ link, name, icon, username, displayName }) => (<li className="flex flex-initial" key={name}>
					<Link className="flex justify-center items-center gap-2" href={link}>
						{icon && <img width="30" height="30" src={icon === null || icon === void 0 ? void 0 : icon.src} alt={name}/>}
						<span className="text-white">{displayName || username}</span>
					</Link>
				</li>))}
		</ul>);
};
const CreatorDataActionButtons = ({ isOwnProfile, isFollowing }) => {
    const { followMutation, onAddToProgram, isTransformableToCreator } = useCreatorDataActionButtons();
    const { isAdmin } = useIsAdmin();
    const { isBrand } = useIsBrand();
    const isAdminOrBrand = isBrand || isAdmin;
    return (<div className="flex flex-initial flex-col gap-2 sm:items-end">
			{isTransformableToCreator && <BecomeCreatorButton />}
			{isAdminOrBrand && (<ButtonNewV2 onClick={onAddToProgram} variant={ButtonNewVariant.Secondary}>
					<span className="uppercase tracking-wider font-normal text-sm">Add to Program</span>
				</ButtonNewV2>)}
			{!(isOwnProfile || isAdminOrBrand) && (<BaseTooltip tooltip={(<BaseTooltipContent>
							{isFollowing ? 'Unfollow this creator' : 'Follow this creator to stay updated with their latest content'}
						</BaseTooltipContent>)}>
					<ButtonNewV2 className={twMerge('flex justify-center items-center gap-2', isFollowing && 'animate-pop-up')} disabled={followMutation.isPending} onClick={() => followMutation.mutateAsync()}>
						{isFollowing ? <FaHeart className="text-red-500"/> : <FaRegHeart />}
					</ButtonNewV2>
				</BaseTooltip>)}
		</div>);
};
export const CreatorDataInfo = () => {
    var _a, _b;
    const { creatorData, isOwnProfile, socialLinks } = useCreatorDataInfo();
    const { userData } = useUserData();
    const { isLive, stream } = useCreatorLiveStream();
    return (<div className="flex flex-col border-white border p-4 gap-4 justify-between">
			<div className="flex flex-col sm:flex-row flex-initial items-center sm:items-start gap-4">
				<div className="relative">
					<CreatorDataAvatar image={creatorData === null || creatorData === void 0 ? void 0 : creatorData.image} alt={creatorData === null || creatorData === void 0 ? void 0 : creatorData.displayName}/>
					{isLive && (<span className="absolute bottom-0 right-0 w-4 h-4 bg-red-600 rounded-full border-2 border-dark-4 animate-pulse"/>)}
				</div>
				<div className="flex flex-col gap-2 min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<CreatorDataName name={creatorData === null || creatorData === void 0 ? void 0 : creatorData.displayName}/>
						{isLive && <LiveIndicatorBadge viewerCount={stream === null || stream === void 0 ? void 0 : stream.viewerCount}/>}
					</div>
					<CreatorDataStats followers={creatorData === null || creatorData === void 0 ? void 0 : creatorData.followerCount} ccv={(_a = creatorData === null || creatorData === void 0 ? void 0 : creatorData.ccv) !== null && _a !== void 0 ? _a : (_b = userData === null || userData === void 0 ? void 0 : userData.creatorData) === null || _b === void 0 ? void 0 : _b.ccv} isOwnProfile={isOwnProfile}/>
				</div>
				<CreatorDataActionButtons isFollowing={Boolean(creatorData === null || creatorData === void 0 ? void 0 : creatorData.following)} isOwnProfile={Boolean(isOwnProfile)}/>
			</div>
			<div className="flex flex-col p-4 gap-2">
				<CreatorDataSocialLinks links={socialLinks}/>
			</div>
		</div>);
};
